import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getLoyaltyProgram,
  saveLoyaltyProgram,
  getLoyaltyRules,
  createLoyaltyRule,
  updateLoyaltyRule,
  deleteLoyaltyRule,
  getLoyaltyCustomers,
  getVenueAllItems,
  getVenues,
} from "@/api/endpoints";
import type { LoyaltyRule } from "@/types";
import { useI18n } from "@/i18n";
import { UpgradeDialog } from "@/components/upgrade-dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Plus, Edit2, Trash2, Lock, Check } from "lucide-react";

const PLAN_RANK: Record<string, number> = { FREE: 0, PRO: 1, BUSINESS: 2 };

export function LoyaltyPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const { t } = useI18n();

  const { data: venues, isLoading: venuesLoading } = useQuery({
    queryKey: ["venues"],
    queryFn: getVenues,
  });

  const topPlan = (venues || []).reduce<string>(
    (max, v) => ((PLAN_RANK[v.planType] ?? 0) > (PLAN_RANK[max] ?? 0) ? v.planType : max),
    "FREE"
  );
  const isLoyaltyEnabled = topPlan === "PRO" || topPlan === "BUSINESS";

  const [upgradeOpen, setUpgradeOpen] = useState(false);

  if (venuesLoading) {
    return <p className="py-8 text-center text-muted-foreground">{t("loading")}</p>;
  }

  if (!isLoyaltyEnabled) {
    return (
      <div className="max-w-2xl">
        <h1 className="mb-6 text-2xl font-bold">{t("loyalty")}</h1>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-primary/10">
              <Lock className="size-8 text-primary" />
            </div>
            <h2 className="text-xl font-bold">{t("upgrade_loyalty")}</h2>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              {t("upgrade_feature_desc").replace("{{feature}}", t("upgrade_loyalty"))}
            </p>
            <Button className="mt-6" onClick={() => setUpgradeOpen(true)}>
              {t("upgrade_cta")}
            </Button>
          </CardContent>
        </Card>
        <UpgradeDialog
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          feature={t("upgrade_loyalty")}
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <h1 className="mb-6 text-2xl font-bold">{t("loyalty")}</h1>
      <Tabs defaultValue="program">
        <TabsList>
          <TabsTrigger value="program">{t("loyalty_program")}</TabsTrigger>
          <TabsTrigger value="rules">{t("loyalty_rules")}</TabsTrigger>
          <TabsTrigger value="customers">{t("loyalty_customers")}</TabsTrigger>
        </TabsList>
        <TabsContent value="program" className="pt-4">
          <ProgramTab />
        </TabsContent>
        <TabsContent value="rules" className="pt-4">
          <RulesTab venueId={venueId!} />
        </TabsContent>
        <TabsContent value="customers" className="pt-4">
          <CustomersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProgramTab() {
  const { t } = useI18n();
  const qc = useQueryClient();

  const { data: program, isLoading } = useQuery({
    queryKey: ["loyalty", "program"],
    queryFn: getLoyaltyProgram,
  });

  const [name, setName] = useState("");
  const [scope, setScope] = useState<"all_venues" | "per_venue">("all_venues");
  const [pointsPerCurrency, setPointsPerCurrency] = useState("1");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (program) {
      setName(program.name);
      setScope(program.scope);
      setPointsPerCurrency(String(program.pointsPerCurrency));
      setIsActive(program.isActive);
    }
  }, [program]);

  const saveMutation = useMutation({
    mutationFn: () =>
      saveLoyaltyProgram({
        name,
        pointsPerCurrency: parseFloat(pointsPerCurrency) || 1,
        isActive,
        scope,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["loyalty", "program"] }),
  });

  if (isLoading) {
    return <p className="py-8 text-center text-muted-foreground">{t("loading")}</p>;
  }

  return (
    <Card>
      <CardContent className="pt-6">
        {!program && (
          <p className="mb-4 rounded-md bg-muted p-3 text-sm text-muted-foreground">
            {t("loyalty_no_program")}
          </p>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) saveMutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <Label>{t("name")}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="HereMenu Sadakat"
            />
          </div>

          <div>
            <Label>{t("loyalty_scope")}</Label>
            <Select
              value={scope}
              onValueChange={(v) => setScope((v as "all_venues" | "per_venue") || "all_venues")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_venues">{t("loyalty_scope_all_venues")}</SelectItem>
                <SelectItem value="per_venue">{t("loyalty_scope_per_venue")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{t("loyalty_points_per_currency")}</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              value={pointsPerCurrency}
              onChange={(e) => setPointsPerCurrency(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t("loyalty_points_per_currency_hint")}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>{t("active")}</Label>
          </div>

          <Button type="submit" disabled={!name.trim() || saveMutation.isPending}>
            {saveMutation.isPending ? t("saving") : program ? t("save") : t("loyalty_create_program")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function RulesTab({ venueId }: { venueId: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["loyalty", "rules"],
    queryFn: getLoyaltyRules,
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editRule, setEditRule] = useState<LoyaltyRule | null>(null);

  const delMutation = useMutation({
    mutationFn: deleteLoyaltyRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["loyalty", "rules"] }),
  });

  if (isLoading) {
    return <p className="py-8 text-center text-muted-foreground">{t("loading")}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-end">
        <Button
          onClick={() => {
            setEditRule(null);
            setFormOpen(true);
          }}
        >
          <Plus className="mr-2 size-4" /> {t("loyalty_add_rule")}
        </Button>
      </div>

      {!rules.length ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <Heart className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("loyalty_no_rules")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((r) => (
            <Card key={r._id}>
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <Badge variant={r.type === "earn" ? "default" : "secondary"}>
                        {r.type === "earn" ? t("loyalty_rule_earn") : t("loyalty_rule_redeem")}
                      </Badge>
                      <Badge variant="outline">
                        {t(("loyalty_trigger_" + r.trigger) as Parameters<typeof t>[0])}
                      </Badge>
                      <span className="text-sm font-bold text-primary">
                        {r.points} {t("loyalty_points")}
                      </span>
                      {!r.isActive && (
                        <Badge variant="outline" className="text-destructive">
                          {t("staff_inactive")}
                        </Badge>
                      )}
                    </div>
                    {r.trigger === "min_amount" && r.minAmount !== undefined && (
                      <p className="text-xs text-muted-foreground">≥ {r.minAmount} ₺</p>
                    )}
                    {r.rewardDescription && (
                      <p className="mt-1 text-sm">{r.rewardDescription}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={() => {
                        setEditRule(r);
                        setFormOpen(true);
                      }}
                    >
                      <Edit2 className="size-4" />
                    </Button>
                    <ConfirmDialog
                      trigger={
                        <Button variant="ghost" size="icon" className="size-8">
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      }
                      title={t("delete")}
                      description={t("loyalty_delete_rule")}
                      confirmLabel={t("delete")}
                      onConfirm={() => delMutation.mutate(r._id)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <RuleFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditRule(null);
        }}
        rule={editRule}
        venueId={venueId}
      />
    </div>
  );
}

function RuleFormDialog({
  open,
  onClose,
  rule,
  venueId,
}: {
  open: boolean;
  onClose: () => void;
  rule: LoyaltyRule | null;
  venueId: string;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();

  const [type, setType] = useState<"earn" | "redeem">("earn");
  const [trigger, setTrigger] = useState<"any_order" | "min_amount" | "specific_item">("any_order");
  const [points, setPoints] = useState("10");
  const [minAmount, setMinAmount] = useState("");
  const [itemId, setItemId] = useState("");
  const [rewardDescription, setRewardDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      setType(rule?.type ?? "earn");
      setTrigger(rule?.trigger ?? "any_order");
      setPoints(rule ? String(rule.points) : "10");
      setMinAmount(rule?.minAmount !== undefined ? String(rule.minAmount) : "");
      setItemId(rule?.itemId ?? "");
      setRewardDescription(rule?.rewardDescription ?? "");
      setIsActive(rule?.isActive ?? true);
    }
  }, [open, rule]);

  const { data: cats = [] } = useQuery({
    queryKey: ["all-items", venueId],
    queryFn: () => getVenueAllItems(venueId),
    enabled: open && trigger === "specific_item",
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Partial<LoyaltyRule> = {
        type,
        trigger,
        points: parseInt(points, 10) || 1,
        isActive,
      };
      if (trigger === "min_amount") {
        payload.minAmount = parseFloat(minAmount) || 0;
      }
      if (trigger === "specific_item") {
        payload.itemId = itemId;
      }
      if (type === "redeem") {
        payload.rewardDescription = rewardDescription || undefined;
      }
      return rule ? updateLoyaltyRule(rule._id, payload) : createLoyaltyRule(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loyalty", "rules"] });
      onClose();
    },
  });

  const canSubmit =
    !!points &&
    parseInt(points, 10) > 0 &&
    (trigger !== "min_amount" || (!!minAmount && parseFloat(minAmount) > 0)) &&
    (trigger !== "specific_item" || !!itemId);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{rule ? t("loyalty_edit_rule") : t("loyalty_add_rule")}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) saveMutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <Label>{t("loyalty_rule_type")}</Label>
            <Select value={type} onValueChange={(v) => setType(v as "earn" | "redeem")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="earn">{t("loyalty_rule_earn")}</SelectItem>
                <SelectItem value="redeem">{t("loyalty_rule_redeem")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{t("loyalty_trigger")}</Label>
            <Select value={trigger} onValueChange={(v) => setTrigger(v as typeof trigger)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any_order">{t("loyalty_trigger_any_order")}</SelectItem>
                <SelectItem value="min_amount">{t("loyalty_trigger_min_amount")}</SelectItem>
                <SelectItem value="specific_item">{t("loyalty_trigger_specific_item")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="capitalize">
              {t("loyalty_points")} <span className="text-destructive">*</span>
            </Label>
            <Input
              type="number"
              min="1"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
            />
          </div>

          {trigger === "min_amount" && (
            <div>
              <Label>
                {t("loyalty_min_amount")} (₺) <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
              />
            </div>
          )}

          {trigger === "specific_item" && (
            <div>
              <Label>
                {t("loyalty_item")} <span className="text-destructive">*</span>
              </Label>
              <Select value={itemId} onValueChange={(v) => setItemId(v ?? "")}>
                <SelectTrigger>
                  <SelectValue placeholder={t("search")} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {cats.map((c) => (
                    <SelectGroup key={c._id}>
                      <SelectLabel>{c.name}</SelectLabel>
                      {c.items.map((i) => (
                        <SelectItem key={i._id} value={i._id}>
                          {i.name}
                          <span className="ml-2 text-muted-foreground">{i.price}₺</span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {type === "redeem" && (
            <div>
              <Label>{t("loyalty_reward_description")}</Label>
              <Textarea
                value={rewardDescription}
                onChange={(e) => setRewardDescription(e.target.value)}
                placeholder="Bedava kahve / %20 indirim..."
                rows={2}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>{t("active")}</Label>
          </div>

          <Button type="submit" className="w-full" disabled={!canSubmit || saveMutation.isPending}>
            {saveMutation.isPending ? t("saving") : t("save")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CustomersTab() {
  const { t } = useI18n();
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ["loyalty", "customers", page],
    queryFn: () => getLoyaltyCustomers({ limit, offset: page * limit }),
  });

  if (isLoading) {
    return <p className="py-8 text-center text-muted-foreground">{t("loading")}</p>;
  }

  const customers = data?.customers ?? [];
  const total = data?.total ?? 0;

  if (!customers.length) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
        <Heart className="mb-4 size-12 text-muted-foreground" />
        <p className="text-muted-foreground">{t("loyalty_no_customers")}</p>
      </div>
    );
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="overflow-hidden rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Email</th>
              <th className="px-4 py-2 text-right font-medium capitalize">{t("loyalty_points")}</th>
              <th className="hidden px-4 py-2 text-right font-medium sm:table-cell">
                {t("loyalty_total_earned")}
              </th>
              <th className="hidden px-4 py-2 text-right font-medium sm:table-cell">
                {t("loyalty_total_redeemed")}
              </th>
              <th className="hidden px-4 py-2 text-right font-medium md:table-cell">
                {t("loyalty_joined_at")}
              </th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c._id} className="border-t">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span>{c.customer?.email ?? "—"}</span>
                    {c.customer?.isVerified && <Check className="size-3.5 text-green-600" />}
                  </div>
                  {c.customer?.name && (
                    <p className="text-xs text-muted-foreground">{c.customer.name}</p>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-primary">{c.points}</td>
                <td className="hidden px-4 py-2.5 text-right text-muted-foreground sm:table-cell">
                  {c.totalEarned}
                </td>
                <td className="hidden px-4 py-2.5 text-right text-muted-foreground sm:table-cell">
                  {c.totalRedeemed}
                </td>
                <td className="hidden px-4 py-2.5 text-right text-xs text-muted-foreground md:table-cell">
                  {c.customer?.createdAt
                    ? new Date(c.customer.createdAt).toLocaleDateString("tr-TR")
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {customers.length} / {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              {t("back")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("next")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
